/**
 * SVG Section Shape Renderer.
 *
 * Renders a yard section as the appropriate SVG shape (rect, circle,
 * polygon) with its label, scheme badge, capacity bar, and asset
 * indicators. Used within the SVG canvas of the yard map editor.
 */

import type { ShapeType, Point } from "./shapeUtils";
import { getShapeVertices, pointsToSvgString } from "./shapeUtils";

interface SectionShapeProps {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  label: string;
  shapeType: ShapeType;
  points?: Point[] | null;
  rotation?: number;
  assetCount: number;
  capacity: number | null;
  isSelected: boolean;
  schemeBadge?: {
    name: string;
    locationCount: number;
    bg: string;
    text: string;
  } | null;
  /** First few assets for dot indicators on the shape. */
  assets?: Array<{ id: string; sku: string; name: string; status: string }>;
  scale: number;
  locked?: boolean;
  onClick: () => void;
  onPointerDown?: (e: React.PointerEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

function SectionShape({
  id,
  x,
  y,
  width,
  height,
  color,
  label,
  shapeType,
  points,
  rotation,
  assetCount,
  capacity,
  isSelected,
  schemeBadge,
  assets,
  scale,
  locked,
  onClick,
  onPointerDown,
  onContextMenu,
}: SectionShapeProps) {
  const fillOpacity = 0.12;
  const strokeWidth = isSelected ? 2.5 : 1.5;
  const strokeColor = isSelected ? "#7c3aed" : `${color}88`;

  // Label bar height in yard units
  const labelBarH = 18 / scale;
  const fontSize = Math.max(9, 11 / scale);
  const smallFontSize = Math.max(7, 9 / scale);

  // Transform for rotation
  const cx = x + width / 2;
  const cy = y + height / 2;
  const transform = rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined;

  // Render the shape element
  const renderShape = () => {
    if (shapeType === "circle") {
      const rx = width / 2;
      const ry = height / 2;
      return (
        <ellipse
          cx={x + rx}
          cy={y + ry}
          rx={rx}
          ry={ry}
          fill={color}
          fillOpacity={fillOpacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    }

    if (shapeType === "polygon" && points && points.length >= 3) {
      // Custom polygons store absolute vertices; translate them so the shape
      // follows the section's x/y as it is dragged (x/y track the bounding-box
      // origin, which equals the min vertex at creation time).
      const minX = Math.min(...points.map((p) => p.x));
      const minY = Math.min(...points.map((p) => p.y));
      const dx = x - minX;
      const dy = y - minY;
      return (
        <polygon
          points={pointsToSvgString(points)}
          transform={dx || dy ? `translate(${dx} ${dy})` : undefined}
          fill={color}
          fillOpacity={fillOpacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    }

    if (shapeType === "rectangle") {
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={4 / scale}
          ry={4 / scale}
          fill={color}
          fillOpacity={fillOpacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    }

    // Preset polygon shapes (l_shape, t_shape, triangle)
    const verts = getShapeVertices(shapeType, x, y, width, height);
    return (
      <polygon
        points={pointsToSvgString(verts)}
        fill={color}
        fillOpacity={fillOpacity}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  };

  // Capacity fill percentage
  const fillPct = capacity ? Math.min(100, (assetCount / capacity) * 100) : 0;
  const fillBarColor =
    fillPct > 90 ? "#EF4444" : fillPct > 70 ? "#F59E0B" : color;

  return (
    <g
      data-section-id={id}
      onClick={onClick}
      onPointerDown={locked ? undefined : onPointerDown}
      onContextMenu={onContextMenu}
      style={{
        cursor: locked ? "not-allowed" : onPointerDown ? "grab" : "pointer",
      }}
      transform={transform}
    >
      {/* Shape */}
      {renderShape()}
      {locked && (
        <text
          x={x + width - 4 / scale}
          y={y + 12 / scale}
          fontSize={11 / scale}
          textAnchor="end"
          style={{ pointerEvents: "none" }}
        >
          🔒
        </text>
      )}

      {/* Selection ring */}
      {isSelected && shapeType === "rectangle" && (
        <rect
          x={x - 3}
          y={y - 3}
          width={width + 6}
          height={height + 6}
          rx={6 / scale}
          ry={6 / scale}
          fill="none"
          stroke="#7c3aed"
          strokeWidth={1}
          strokeOpacity={0.3}
        />
      )}

      {/* Label bar background */}
      <rect
        x={x}
        y={y}
        width={width}
        height={labelBarH}
        rx={shapeType === "rectangle" ? 4 / scale : 0}
        fill={color}
      />

      {/* Label text */}
      <text
        x={x + 4 / scale}
        y={y + labelBarH * 0.7}
        fontSize={fontSize}
        fontWeight="bold"
        fill="white"
        style={{ pointerEvents: "none" }}
      >
        {label.length > 15 ? label.slice(0, 14) + "…" : label}
      </text>

      {/* Asset count */}
      <text
        x={x + width - 4 / scale}
        y={y + labelBarH * 0.7}
        fontSize={smallFontSize}
        fill="white"
        fillOpacity={0.8}
        textAnchor="end"
        style={{ pointerEvents: "none" }}
      >
        {assetCount}
        {capacity ? `/${capacity}` : ""}
      </text>

      {/* Scheme badge */}
      {schemeBadge && (
        <g>
          <rect
            x={x + 3 / scale}
            y={y + labelBarH + 2 / scale}
            width={Math.min(width - 6 / scale, 90 / scale)}
            height={12 / scale}
            rx={6 / scale}
            fill={color}
            fillOpacity={0.15}
          />
          <text
            x={x + 8 / scale}
            y={y + labelBarH + 11 / scale}
            fontSize={Math.max(6, 8 / scale)}
            fill={color}
            style={{ pointerEvents: "none" }}
          >
            {schemeBadge.name.length > 12
              ? schemeBadge.name.slice(0, 11) + "…"
              : schemeBadge.name}{" "}
            ({schemeBadge.locationCount})
          </text>
        </g>
      )}

      {/* Asset status dots (for sections without a scheme, max 6 shown) */}
      {!schemeBadge && assets && assets.length > 0 && (
        <g>
          {assets.slice(0, 6).map((a, i) => {
            const dotR = 3 / scale;
            const dotSpacing = 8 / scale;
            const startY = y + labelBarH + 6 / scale;
            const dotX = x + 6 / scale + (i % 3) * dotSpacing;
            const dotY = startY + Math.floor(i / 3) * dotSpacing;
            const dotColor =
              a.status === "Received" || a.status === "Available"
                ? "#10B981"
                : a.status === "In Service" || a.status === "Reserved"
                  ? "#F59E0B"
                  : a.status === "Sold"
                    ? "#EF4444"
                    : "#94A3B8";
            return (
              <circle
                key={a.id}
                cx={dotX}
                cy={dotY}
                r={dotR}
                fill={dotColor}
                fillOpacity={0.8}
              >
                <title>
                  {a.sku} — {a.name} ({a.status})
                </title>
              </circle>
            );
          })}
          {assetCount > 6 && (
            <text
              x={x + 6 / scale + 3 * (8 / scale)}
              y={y + labelBarH + 6 / scale + 8 / scale + 3 / scale}
              fontSize={Math.max(6, 7 / scale)}
              fill="var(--text-tertiary, #94a3b8)"
              style={{ pointerEvents: "none" }}
            >
              +{assetCount - 6}
            </text>
          )}
        </g>
      )}

      {/* Capacity fill bar */}
      {capacity && fillPct > 0 && (
        <g>
          <rect
            x={x + 3 / scale}
            y={y + height - 6 / scale}
            width={width - 6 / scale}
            height={3 / scale}
            rx={1.5 / scale}
            fill={color}
            fillOpacity={0.2}
          />
          <rect
            x={x + 3 / scale}
            y={y + height - 6 / scale}
            width={((width - 6 / scale) * fillPct) / 100}
            height={3 / scale}
            rx={1.5 / scale}
            fill={fillBarColor}
          />
        </g>
      )}
    </g>
  );
}

export default SectionShape;
